import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetSignOnPolicyApplicationErrors,
  GetSignOnPolicyResponse,
  SignOnPolicy,
  SignOnPolicyUpdatedEnvelope,
  UpdateSignOnPolicyApplicationErrors,
  getSignOnPolicy as universalGetSignOnPolicy,
  updateSignOnPolicy as universalUpdateSignOnPolicy,
} from './iam-signon-v1-sign-on-policy-sign-on-policy.universal.js';

const TRANSFORM_PATHS_0 = [
  { path: 'entity.createdDate' },
  { path: 'entity.updatedDate' },
  { path: 'metadata.eventTime' },
];
export const __metadata = { PACKAGE_NAME: '@wix/identity' };

export function getSignOnPolicy(
  httpClient: HttpClient
): GetSignOnPolicySignature {
  return () =>
    universalGetSignOnPolicy(
      // @ts-ignore
      { httpClient }
    );
}

interface GetSignOnPolicySignature {
  /**
   * Retrieves the calling site's sign-on policy.
   *
   * The request takes no parameters. The site is resolved from the request context.
   *
   * A site that has never updated its policy still gets a successful response,
   * with each requirement set to its default of `false`.
   *
   * The response currently contains `requireOwnerApproval` and `requireEmailVerification`.
   *
   * This method needs a minimum amount of time left on the request deadline,
   * 200 ms by default. A call that arrives with less time remaining fails with
   * `INSUFFICIENT_DEADLINE_BUDGET`.
   */
  (): Promise<
    NonNullablePaths<
      GetSignOnPolicyResponse,
      | `signOnPolicy.requireOwnerApproval`
      | `signOnPolicy.requireEmailVerification`,
      1
    > & {
      __applicationErrorsType?: GetSignOnPolicyApplicationErrors;
    }
  >;
}

export function updateSignOnPolicy(
  httpClient: HttpClient
): UpdateSignOnPolicySignature {
  return (signOnPolicy: SignOnPolicy) =>
    universalUpdateSignOnPolicy(
      signOnPolicy,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateSignOnPolicySignature {
  /**
   * Updates the calling site's sign-on policy.
   *
   * Partial updates are supported. Specify only the requirements you want to change,
   * and the rest keep their current values.
   *
   * Changing the policy affects members who sign up from that point on.
   * Existing members aren't re-evaluated, so turning on `requireOwnerApproval`
   * doesn't move already-active members back to pending.
   *
   * Fails with `NOT_FOUND` when the site doesn't have a sign-on policy yet,
   * and with `INVALID_ARGUMENT` when the request contains an unsupported value.
   * @param - Sign-on policy to update. Can be partial, and omitted requirements keep their current values.
   * @returns Updated sign-on policy.
   */
  (signOnPolicy: SignOnPolicy): Promise<
    NonNullablePaths<
      SignOnPolicy,
      `requireOwnerApproval` | `requireEmailVerification`,
      0
    > & {
      __applicationErrorsType?: UpdateSignOnPolicyApplicationErrors;
    }
  >;
}

export const onSignOnPolicyUpdated = /*#__PURE__*/ EventDefinition(
  'wix.iam.signon.v1.sign_on_policy_updated',
  true,
  (event: SignOnPolicyUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: TRANSFORM_PATHS_0,
        },
      ])
    )
)<SignOnPolicyUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Asset,
  BaseEventMetadata,
  DeleteContext,
  DeleteStatus,
  DomainEvent,
  DomainEventBodyOneOf,
  EditorlessAssigned,
  EditorlessUnassigned,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GetSignOnPolicyRequest,
  GetSignOnPolicyResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  MetaSiteSpecialEvent,
  MetaSiteSpecialEventPayloadOneOf,
  Namespace,
  NamespaceChanged,
  OdeditorAssigned,
  OdeditorUnassigned,
  PicassoAssigned,
  PicassoUnassigned,
  RestoreInfo,
  ServiceProvisioned,
  ServiceRemoved,
  SignOnPolicy,
  SignOnPolicyUpdatedEnvelope,
  SiteCreated,
  SiteCreatedContext,
  SiteDeleted,
  SiteHardDeleted,
  SiteMarkedAsTemplate,
  SiteMarkedAsWixSite,
  SitePublished,
  SitePurgedExternally,
  SiteRenamed,
  SiteTransferred,
  SiteUndeleted,
  SiteUnpublished,
  SiteUrlChanged,
  State,
  StudioAssigned,
  StudioTwoAssigned,
  StudioTwoUnassigned,
  StudioUnassigned,
  UpdateSignOnPolicyRequest,
  UpdateSignOnPolicyResponse,
  UserDomainMediaDisabled,
  UserDomainMediaEnabled,
  WebhookIdentityType,
  WixelAssigned,
  WixelUnassigned,
} from './iam-signon-v1-sign-on-policy-sign-on-policy.universal.js';
